import type React from "react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CustomCollapsibleItem } from "@/components/ui/custom-collapsible-item";
import type { VisitRecording } from "@/lib/visit-content-queries";
import { CardContentMainTranscription } from "@/pages/Visit/components/CardContentMainTranscription";
import type { FileData } from "@/services/files";
import { OwnedAudioPlayer } from "./owned-audio-player";
import { SpeakerTranscript } from "./SpeakerTranscript";
import { useTranscriptV3 } from "./transcript-style";

interface TranscriptionsCardProps {
  file: FileData | null;
  multiRecordingFiles: VisitRecording[];
  expandedItems: string[];
  toggleExpanded: (id: string) => void;
  // Workspace transcript bar: just the recordings, without the card frame
  // and title, and without the version picker (it sits on the note).
  embedded?: boolean;
}

export const TranscriptionsCard: React.FC<TranscriptionsCardProps> = ({
  file,
  multiRecordingFiles,
  expandedItems,
  toggleExpanded,
  embedded = false,
}) => {
  const { t } = useTranslation();

  if (embedded) {
    return (
      <div className="divide-y">
        {file && (
          <div className="py-3">
            <CardContentMainTranscription
              expanded={expandedItems.includes(file.public_id)}
              file={file}
              id={file.public_id}
              showVersionSelect={false}
              onToggle={() => {
                toggleExpanded(file.public_id);
              }}
            />
          </div>
        )}
        {multiRecordingFiles.map((record, index) => (
          <div key={record.public_id} className="py-3">
            <RecordingItem
              expanded={expandedItems.includes(record.public_id)}
              index={index}
              record={record}
              onToggle={() => {
                toggleExpanded(record.public_id);
              }}
            />
          </div>
        ))}
      </div>
    );
  }

  return (
    <Card className="mt-6 rounded-lg max-sm:-mx-4 max-sm:rounded-none max-sm:border-x-0 max-sm:shadow-none">
      <CardHeader className="rounded-t-lg bg-grey-white p-0 max-sm:rounded-none">
        <CardTitle className="p-6 text-lg font-bold max-sm:px-4">
          {t("visit.transcriptions")}
        </CardTitle>
      </CardHeader>
      <CardContent className="rounded-b-md p-6 max-sm:px-4">
        {file ? (
          <CardContentMainTranscription
            expanded={expandedItems.includes(file.public_id)}
            file={file}
            id={file.public_id}
            onToggle={() => {
              toggleExpanded(file.public_id);
            }}
          />
        ) : (
          <div className="flex items-center justify-center py-4">
            <div className="w-5 h-5 border-2 border-t-primary border-primary/50 rounded-full animate-spin" />
          </div>
        )}
      </CardContent>
      {multiRecordingFiles.map((record, index) => (
        <CardContent
          key={record.public_id}
          className="border-t p-6 max-sm:px-4"
        >
          <RecordingItem
            expanded={expandedItems.includes(record.public_id)}
            index={index}
            record={record}
            onToggle={() => {
              toggleExpanded(record.public_id);
            }}
          />
        </CardContent>
      ))}
    </Card>
  );
};

const RecordingItem = ({
  record,
  index,
  expanded,
  onToggle,
}: {
  record: VisitRecording;
  index: number;
  expanded: boolean;
  onToggle: () => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const v3 = useTranscriptV3();
  return record.status?.toLowerCase() === "processing" ? (
    <div className="flex items-center justify-center py-4">
      <div className="w-5 h-5 border-2 border-t-primary border-primary/50 rounded-full animate-spin" />
    </div>
  ) : (
    <CustomCollapsibleItem
      content={
        <>
          <OwnedAudioPlayer source={record.name} />
          <SpeakerTranscript
            recordingPublicId={record.public_id}
            text={record.transcription}
          />
        </>
      }
      expanded={expanded}
      id={record.public_id}
      title={
        v3 ? (
          <h3 className="font-v3 text-[13px] font-semibold uppercase tracking-[0.04em] text-v3-text-3">
            {t("visit.recording")} {index + 2}
          </h3>
        ) : (
          <h3 className="text-lg font-bold">
            {t("visit.recording")} #{index + 2}
          </h3>
        )
      }
      onToggle={onToggle}
    />
  );
};
